import { encryptData, decryptData } from "../settings/cryptoHelper"

const DEFAULT_ADMIN_ROUTE = "/search"

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = (num) => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "")

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

export const formatDate = (
  value,
  formatting = { month: "short", day: "numeric", year: "numeric" }
) => {
  if (!value) return value
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: "short", day: "numeric" }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" }
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value))
}


const STORAGE_KEY = "userData"
// 🔥 CACHE (IMPORTANT)
let cachedUser = null
let isCacheLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setUserData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY, encrypted)

  // ✅ update cache
  cachedUser = data
  isCacheLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getUserData = () => {
  // ✅ return cache if already loaded
  if (isCacheLoaded) return cachedUser

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      cachedUser = null
      isCacheLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedUser = decrypted
      isCacheLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY, encrypted)

    cachedUser = parsed
    isCacheLoaded = true
    return parsed

  } catch {
    cachedUser = null
    isCacheLoaded = true
    return null
  }
}

// ❌ Remove only user data + reset cache
export const removeUserData = () => {
  localStorage.removeItem(STORAGE_KEY)

  cachedUser = null
  isCacheLoaded = false
}

// ✅ Session check (FAST - no re-decrypt)
export const isUserLoggedInSession = () => {
  return !!getUserData()
}

// ✅ Get user safely (used in 200 components)
export const isUserLoggedIn = () => {
  return getUserData()
}


// ==============================
// JS SCRIPT DATA
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_JS = "JsScriptData"
let cachedJS = null
let isCacheJSLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setJSData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_JS, encrypted)

  // ✅ update cache
  cachedJS = data
  isCacheJSLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getJSData = () => {
  // ✅ return cache if already loaded
  if (isCacheJSLoaded) return cachedJS

  try {
    const stored = localStorage.getItem(STORAGE_KEY_JS)
    if (!stored) {
      cachedJS = null
      isCacheJSLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedJS = decrypted
      isCacheJSLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_JS, encrypted)

    cachedJS = parsed
    isCacheJSLoaded = true
    return parsed

  } catch {
    cachedJS = null
    isCacheJSLoaded = true
    return null
  }
}


// ==============================
// MERCHANT ONE DATA
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_MO = "merchantOne"
let cachedMO = null
let isCacheMOLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setMOData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_MO, encrypted)

  // ✅ update cache
  cachedMO = data
  isCacheMOLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getMOData = () => {
  // ✅ return cache if already loaded
  if (isCacheMOLoaded) return cachedMO

  try {
    const stored = localStorage.getItem(STORAGE_KEY_MO)
    if (!stored) {
      cachedMO = null
      isCacheMOLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedMO = decrypted
      isCacheMOLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_MO, encrypted)

    cachedMO = parsed
    isCacheMOLoaded = true
    return parsed

  } catch {
    cachedMO = null
    isCacheMOLoaded = true
    return null
  }
}

// ==============================
// AZURE B2C DATA
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_AB2C = "azureAdB2C"
let cachedAB2C = null
let isCacheAB2CLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setAB2CData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_AB2C, encrypted)

  // ✅ update cache
  cachedAB2C = data
  isCacheAB2CLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getAB2CData = () => {
  // ✅ return cache if already loaded
  if (isCacheAB2CLoaded) return cachedAB2C

  try {
    const stored = localStorage.getItem(STORAGE_KEY_AB2C)
    if (!stored) {
      cachedAB2C = null
      isCacheAB2CLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedAB2C = decrypted
      isCacheAB2CLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_AB2C, encrypted)

    cachedAB2C = parsed
    isCacheAB2CLoaded = true
    return parsed

  } catch {
    cachedAB2C = null
    isCacheAB2CLoaded = true
    return null
  }
}

// ==============================
// DASHBOARD LINKS DATA
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_DASHBOARD = "DashBoardLink"
let cachedDashboard = null
let isCacheDashboardLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setDashBoardLinks = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_DASHBOARD, encrypted)

  // ✅ update cache
  cachedDashboard = data
  isCacheDashboardLoaded = true
}

// 📤 Get dashboard links (SAFE + MIGRATION + CACHE)
export const getDashBoardLinks = () => {
  // ✅ return cache if already loaded
  if (isCacheDashboardLoaded) return cachedDashboard

  try {
    const stored = localStorage.getItem(STORAGE_KEY_DASHBOARD)
    if (!stored) {
      cachedDashboard = null
      isCacheDashboardLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedDashboard = decrypted
      isCacheDashboardLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_DASHBOARD, encrypted)

    cachedDashboard = parsed
    isCacheDashboardLoaded = true
    return parsed

  } catch {
    cachedDashboard = null
    isCacheDashboardLoaded = true
    return null
  }
}

export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === "admin") return DEFAULT_ADMIN_ROUTE
  if (userRole === "client") return "/access-control"
  return "/login"
}

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#7367f01a", // for option hover bg-color
    primary: "#7367f0", // for selected option bg-color
    neutral10: "#7367f0", // for tags bg-color
    neutral20: "#ededed", // for input border-color
    neutral30: "#ededed" // for input hover border-color
  }
})

window.addEventListener("storage", (e) => {
  if (e.key === STORAGE_KEY) {
    cachedUser = null
    isCacheLoaded = false
  }
  if (e.key === STORAGE_KEY_JS) {
    cachedJS = null
    isCacheJSLoaded = false
  }
  if (e.key === STORAGE_KEY_MO) {
    cachedMO = null
    isCacheMOLoaded = false
  }
  if (e.key === STORAGE_KEY_AB2C) {
    cachedAB2C = null
    isCacheAB2CLoaded = false
  }
  if (e.key === STORAGE_KEY_DASHBOARD) {
    cachedDashboard = null
    isCacheDashboardLoaded = false
  }
})
